import { useEffect, useState } from "react";
import { Plus, BookOpen, Power, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const TYPES = [
  { key: "asset", label: "Assets" },
  { key: "liability", label: "Liabilities" },
  { key: "equity", label: "Equity" },
  { key: "income", label: "Income" },
  { key: "expense", label: "Expenses" },
];
const TYPE_COLOR = {
  asset: "text-blue-700",
  liability: "text-rose-700",
  equity: "text-violet-700",
  income: "text-emerald-700",
  expense: "text-amber-700",
};

export default function ChartOfAccounts() {
  const { currentId } = useCompany();
  const [accounts, setAccounts] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", name: "", type: "expense", subtype: "" });

  const load = () =>
    api.get(`/accounts?include_inactive=${showInactive}`).then((r) => setAccounts(r.data));
  useEffect(() => {
    if (currentId) load();
  }, [currentId, showInactive]);

  const save = async () => {
    if (!form.code.trim() || !form.name.trim()) return toast.error("Code and name are required");
    try {
      await api.post("/accounts", form);
      toast.success("Account added");
      setOpen(false);
      setForm({ code: "", name: "", type: "expense", subtype: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add account");
    }
  };

  const toggleActive = async (a) => {
    await api.put(`/accounts/${a.id}`, { is_active: !a.is_active });
    load();
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Chart of Accounts</h1>
          <p className="text-slate-500 text-sm mt-1">The ledger accounts every transaction posts to.</p>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-slate-500">
            <Switch data-testid="show-inactive-switch" checked={showInactive} onCheckedChange={setShowInactive} />
            Show inactive
          </label>
          <Button data-testid="add-account-btn" onClick={() => setOpen(true)} className="rounded-md bg-slate-900 hover:bg-slate-800">
            <Plus className="h-4 w-4 mr-2" /> New Account
          </Button>
        </div>
      </div>

      <div className="space-y-6">
        {TYPES.map((t) => {
          const rows = accounts.filter((a) => a.type === t.key);
          if (!rows.length) return null;
          return (
            <Card key={t.key} className="rounded-lg border-slate-200 shadow-sm overflow-hidden p-0">
              <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
                <BookOpen className={`h-4 w-4 ${TYPE_COLOR[t.key]}`} />
                <h2 className={`font-heading font-semibold ${TYPE_COLOR[t.key]}`}>{t.label}</h2>
                <span className="text-xs text-slate-400">({rows.length})</span>
              </div>
              <table className="w-full text-sm" data-testid={`coa-${t.key}`}>
                <tbody>
                  {rows.map((a) => (
                    <tr key={a.id} className={`border-b border-slate-50 hover:bg-slate-50 ${!a.is_active ? "opacity-50" : ""}`}>
                      <td className="px-5 py-2.5 w-24 tabular-nums text-slate-500">{a.code}</td>
                      <td className="px-2 py-2.5 font-medium text-slate-900">
                        {a.name}
                        {a.system_tag && (
                          <Lock className="inline h-3 w-3 ml-2 text-slate-400" title="System account" />
                        )}
                      </td>
                      <td className="px-2 py-2.5 text-slate-500">{a.subtype}</td>
                      <td className="px-5 py-2.5 text-right">
                        {!a.is_active && (
                          <Badge variant="outline" className="mr-2 bg-slate-100 text-slate-500 border-slate-200">Inactive</Badge>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          title={a.is_active ? "Deactivate" : "Activate"}
                          data-testid={`toggle-account-${a.id}`}
                          onClick={() => toggleActive(a)}
                        >
                          <Power className={`h-4 w-4 ${a.is_active ? "text-slate-400" : "text-emerald-600"}`} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading">New Account</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Code</Label>
                <Input data-testid="account-code-input" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="mt-1 rounded-md" placeholder="5150" />
              </div>
              <div className="col-span-2">
                <Label>Name</Label>
                <Input data-testid="account-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-md" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="account-type-select" className="mt-1 rounded-md capitalize">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {TYPES.map((t) => (
                      <SelectItem key={t.key} value={t.key} className="capitalize">{t.key}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Subtype (optional)</Label>
                <Input data-testid="account-subtype-input" value={form.subtype} onChange={(e) => setForm({ ...form, subtype: e.target.value })} className="mt-1 rounded-md" placeholder="Operating Expense" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-md" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-account-btn" onClick={save} className="rounded-md bg-slate-900 hover:bg-slate-800">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
